import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { LayoutDashboard, BarChart2, ChevronDown, FlaskConical, GraduationCap, LogOut, Target, Activity, Search, User, Map } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useCert } from "../contexts/CertContext";
import { useAppData } from "../contexts/AppDataContext";
import { clearSession } from "../services/api";
import { Overlay, Spinner } from "../styles/StyledComponents";
import TacticalDatePicker from "./TacticalDatePicker";
import logoImg from "../assets/logo.png";
import { FEATURES } from "../config/features";

const AppShellScrollLock = createGlobalStyle`
  html,
  body,
  #root {
    height: 100%;
    max-height: 100%;
    overflow: hidden !important;
    scrollbar-gutter: auto !important;
    background: #0a0f1a;
  }
`;

// ─── Styled Components (Elite Cockpit HUD) ───────────────────────────────────

const DashboardContainer = styled.div`
  background: #0a0f1a;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  max-height: 100vh;
  max-height: 100dvh;
  display: flex;
  width: 100%;
  max-width: 100vw;
  overflow: hidden;
  color: #e2e8f0;
  background-image:
    linear-gradient(rgba(59, 130, 246, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(59, 130, 246, 0.03) 1px, transparent 1px);
  background-size: 60px 60px;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 240px;
  background: rgba(15, 23, 42, 0.8);
  backdrop-filter: blur(10px);
  border-right: 1px solid #1e293b;
  display: flex;
  flex-direction: column;
  padding: 24px 0;
  z-index: 100;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100%;
  max-height: 100%;
  min-height: 0;

  @media (max-width: 1100px) {
    width: 200px;
  }

  @media (max-width: 900px) {
    width: 100%;
    height: auto;
    max-width: 100vw;
    max-height: none;
    padding: 12px 12px;
    flex-direction: row;
    align-items: center;
    gap: 6px;
    border-right: none;
    border-bottom: 1px solid #1e293b;
    overflow-x: auto;
    overflow-y: hidden;
    flex-wrap: nowrap;
    scrollbar-width: thin;
    scrollbar-color: rgba(72, 118, 190, 0.55) transparent;
    -webkit-overflow-scrolling: touch;
    scroll-snap-type: x proximity;
    scroll-padding-inline: 12px;
    overscroll-behavior-x: contain;
    &::-webkit-scrollbar {
      height: 6px;
    }
    &::-webkit-scrollbar-thumb {
      background: rgba(72, 118, 190, 0.45);
      border-radius: 999px;
    }
  }
`;

const SidebarHeader = styled.div`
  padding: 0 24px 40px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  h1 {
    margin: 0;
  }
  .sector {
    font-size: 0.6rem;
    font-weight: 800;
    color: #475569;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  @media (max-width: 900px) {
    padding: 0 8px 0 4px;
    flex-shrink: 0;
    .brand-title { display: none; }
  }
`;

const SidebarSpacer = styled.div`
  flex: 1;
  @media (max-width: 900px) {
    display: none;
  }
`;


const NavItemStyled = styled(Link)`
  padding: 14px 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${({ $active }) => $active ? '#f8fafc' : '#64748b'};
  background: ${({ $active }) => $active ? 'rgba(0, 209, 255, 0.05)' : 'transparent'};
  border-left: 3px solid ${({ $active }) => $active ? '#00d1ff' : 'transparent'};
  cursor: pointer;
  font-weight: 800;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  transition: all 0.2s;
  text-decoration: none;
  opacity: ${({ $locked }) => ($locked ? 0.55 : 1)};
  &:hover {
    color: #f8fafc;
    background: rgba(255, 255, 255, 0.02);
    border-left-color: ${({ $active }) => $active ? '#00d1ff' : 'rgba(0, 209, 255, 0.3)'};
  }

  @media (max-width: 1100px) {
    padding: 14px 16px;
    gap: 12px;
    font-size: 0.7rem;
  }

  @media (max-width: 900px) {
    padding: 8px 12px;
    min-height: 36px;
    min-width: max-content;
    border-left: none;
    border-bottom: 2px solid ${({ $active }) => $active ? '#00d1ff' : 'transparent'};
    border-radius: 6px;
    flex-shrink: 0;
    white-space: nowrap;
    scroll-snap-align: start;
    &:hover {
      border-left-color: transparent;
      border-bottom-color: ${({ $active }) => $active ? '#00d1ff' : 'rgba(0, 209, 255, 0.3)'};
    }
  }
`;


// ── Main Content Area ──

const MainContentArea = styled.div`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  /* Exam pages lock their own viewport — strip outer scroll/padding.
     We also tighten side padding in exam mode since the chrome is hidden,
     letting the question fill the full width.

     --exam-side-pad is exposed as a CSS custom property so Exam.js
     (specifically the fixed NavGroup footer) can compute its right
     offset against the real gutter value without hardcoding it. The
     variable must stay in sync with the $isExam padding rules
     below — single source of truth, no duplicate magic numbers. */
  --exam-side-pad: 32px;
  height: 100%;
  max-height: 100%;
  padding: ${({ $isExam, $isStudy }) => $isExam ? '0 var(--exam-side-pad) 0' : $isStudy ? '8px 40px 0' : '8px 40px 0'};
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;

  @media (max-width: 1280px) {
    --exam-side-pad: 24px;
    padding: ${({ $isExam, $isStudy }) => $isExam ? '0 var(--exam-side-pad) 0' : $isStudy ? '8px 28px 0' : '8px 28px 0'};
  }
  @media (max-width: 1023px) {
    --exam-side-pad: 16px;
    padding: ${({ $isExam, $isStudy }) => $isExam ? '0 var(--exam-side-pad) 0' : $isStudy ? '8px 16px 0' : '8px 16px 0'};
  }
  @media (max-width: 900px) {
    height: auto;
    max-height: none;
    min-height: 0;
  }
  @media (max-width: 600px) {
    --exam-side-pad: 8px;
    padding: ${({ $isExam, $isStudy }) => $isExam ? '0 var(--exam-side-pad) 0' : $isStudy ? '4px 12px 0' : '4px 12px 0'};
  }
`;

const TopBar = styled.div`
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    margin-bottom: 16px;
    padding-bottom: 16px;
  }

  @media (max-width: 600px) {
    position: relative;

    > div:first-child {
      width: 100%;
      padding-right: 52px;
    }

    > div:last-child {
      position: absolute;
      top: 0;
      right: 0;
    }
  }
`;

const OutletFrame = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  padding-right: ${({ $isExam, $isStudy }) => ($isExam || $isStudy) ? '0' : '10px'};
  padding-bottom: ${({ $isStudy, $isNode, $isSetup }) => ($isStudy || $isNode || $isSetup) ? '0' : '120px'};
  overflow-x: hidden;
  /* Exam scrolls at the page level so the top Exit bar is always
     reachable. Study keeps its own internal scroll; every other page
     scrolls normally. (Only the exam branch changed here.) */
  overflow-y: ${({ $isStudy }) => ($isStudy ? 'hidden' : 'auto')};
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: rgba(72, 118, 190, 0.42) transparent;
  scroll-padding-top: 24px;
  scroll-padding-bottom: ${({ $isStudy, $isNode }) => ($isStudy || $isNode) ? '0' : '100px'};

  &::-webkit-scrollbar:vertical {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(72, 118, 190, 0.34);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(72, 118, 190, 0.56);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  ${({ $isStudy }) => $isStudy && `
    display: flex;
  `}

  @media (max-width: 1280px) {
    padding-bottom: ${({ $isStudy, $isNode, $isSetup }) => ($isStudy || $isNode || $isSetup) ? '0' : '100px'};
  }

  @media (max-width: 1023px) {
    padding-bottom: ${({ $isStudy, $isNode, $isSetup }) => ($isStudy || $isNode || $isSetup) ? '0' : '80px'};
  }
`;




const TelemetryItem = styled.button`
  display: flex;
  align-items: center;
  min-height: 44px;
  gap: 12px;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  .label { font-size: 0.65rem; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; }
  .value { font-size: 0.85rem; color: #f8fafc; font-weight: 700; font-family: 'JetBrains Mono', 'Roboto Mono', monospace; }
  .icon { color: #3b82f6; }
`;

const CertSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: rgba(15, 23, 42, 0.5);
  padding: 8px 16px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  &:hover { border-color: #3b82f6; background: rgba(59, 130, 246, 0.05); }

  .cert-info {
    display: flex;
    flex-direction: column;
    .label { font-size: 0.6rem; color: #64748b; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; }
    .name { font-size: 0.85rem; font-weight: 800; color: #f8fafc; }
  }
`;

const CertDropdownMenu = styled(motion.div)`
  position: absolute;
  top: calc(100% + 12px);
  left: 0;
  width: min(320px, calc(100vw - 32px));
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  flex-direction: column;
  max-height: min(480px, 70vh);
  overflow: hidden;
`;

const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  
  /* Sleek Custom Scrollbar */
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: #334155; }
`;

const SearchWrapper = styled.div`
  padding: 12px;
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  gap: 10px;
  background: rgba(15, 23, 42, 0.3);
  
  input {
    background: transparent;
    border: none;
    color: #f8fafc;
    font-size: 0.85rem;
    font-weight: 600;
    outline: none;
    width: 100%;
    &::placeholder { color: #475569; }
  }
`;

const CategoryHeader = styled.div`
  font-size: 0.65rem;
  font-weight: 800;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  padding: 12px 12px 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  &:not(:first-child) { margin-top: 8px; }
`;


const ProfileChip = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  position: relative;
  .name-info { text-align: right; .user { font-weight: 900; font-size: 0.85rem; color: #f8fafc; } .sub { font-size: 0.7rem; color: #64748b; font-weight: 700; } }
  .avatar {
    width: 36px; height: 36px; border-radius: 50%;
    background: ${({ $color, theme }) => $color ? $color : `linear-gradient(135deg, ${theme.primary}, ${theme.secondary || '#8b5cf6'})`};
    border: 2px solid #3b82f6;
    display: flex; align-items: center; justify-content: center; color: white; font-weight: 800;
    flex-shrink: 0;
  }

  @media (max-width: 600px) {
    .name-info { display: none; }
  }
`;

const ProfileColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
`;

const DropdownMenu = styled(motion.div)`
  position: absolute;
  top: 120%;
  right: 0;
  width: min(220px, calc(100vw - 32px));
  background: #0f172a;
  border: 1px solid #1e293b;
  border-radius: 12px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);
  overflow: hidden;
  z-index: 200;
  padding: 8px;
`;

const MenuBtn = styled.button`
  width: 100%;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  &:hover { background: rgba(59, 130, 246, 0.1); color: #f8fafc; }
  &.danger { color: #ef4444; &:hover { background: rgba(239, 68, 68, 0.1); } }
`;

export default function Layout({ onLogout }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [certDropdownOpen, setCertDropdownOpen] = useState(false);
    const [certSearch, setCertSearch] = useState("");
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [savingDate, setSavingDate] = useState(false);
    const [dateSaveError, setDateSaveError] = useState(false);
    const {
        activeCert,
        setActiveCert,
        CERTS,
        isChangingCert,
        examDate,
        setExamDate,
        loading: catalogLoading,
        catalogReady,
        configError,
        retryConfigs,
    } = useCert();
    const { loading: appLoading, dashboardData, displayName: sharedDisplayName } = useAppData();
    const isOnExam = location.pathname.startsWith("/exam/");
    // Node briefings (/cert/:code/node/:id) share the exam canvas — sidebar
    // and top bar hidden — so the roadmap's learn → practice handoff reads
    // as one continuous full-screen surface instead of a page-in-a-page.
    const roadmapEnabled = FEATURES.study || FEATURES.roadmapV2;
    const isNodeBriefing = roadmapEnabled && /^\/cert\/[^/]+\/node\//.test(location.pathname);
    const roadmapPath = "/study";
    const isStudy = roadmapEnabled && (
        location.pathname === "/study"
        || location.pathname.startsWith("/roadmap-v2")
        || /^\/cert\/[^/]+\/node\//.test(location.pathname)
    );
    const isFullBleed = isOnExam || isNodeBriefing || isStudy;
    // Extract the session ID directly from the URL so we can abandon it on cert switch
    const currentSessionId = isOnExam ? location.pathname.split("/exam/")[1] : null;
    const [pendingCert, setPendingCert] = useState(null);
    const [switchingCert, setSwitchingCert] = useState(false);

    const certRef = useRef(null);
    const profileRef = useRef(null);
    const navRef = useRef(null);
    const pageRef = useRef(null);

    // A new route must not inherit the previous page's scroll offset. Query
    // changes within a page preserve position; reset before the next paint.
    useLayoutEffect(() => {
        if (pageRef.current) pageRef.current.scrollTop = 0;
    }, [location.pathname]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (certRef.current && !certRef.current.contains(event.target)) {
                setCertDropdownOpen(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target)) {
                setDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const displayName = sharedDisplayName || dashboardData?.displayName || (appLoading ? localStorage.getItem("displayName") : null) || "User";
    useEffect(() => {
        if (dashboardData?.displayName) localStorage.setItem("displayName", dashboardData.displayName);
    }, [dashboardData?.displayName]);

    useEffect(() => {
        const nav = navRef.current;
        const activeItem = nav?.querySelector('[data-main-nav-active="true"]');
        if (!nav || !activeItem || typeof window === "undefined") return;
        if (!window.matchMedia("(max-width: 900px)").matches) return;

        const centerActiveItem = () => {
            const targetLeft = activeItem.offsetLeft - ((nav.clientWidth - activeItem.clientWidth) / 2);
            const maxLeft = Math.max(0, nav.scrollWidth - nav.clientWidth);
            nav.scrollTo({
                left: Math.min(Math.max(0, targetLeft), maxLeft),
                behavior: "auto"
            });
        };

        centerActiveItem();
        const frameId = window.requestAnimationFrame(centerActiveItem);
        const timeoutIds = [80, 250, 600].map((delay) => window.setTimeout(centerActiveItem, delay));
        return () => {
            window.cancelAnimationFrame(frameId);
            timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
        };
    }, [location.pathname, isStudy, appLoading]);

    const initial = displayName.charAt(0).toUpperCase();
    const isActive = (path) => location.pathname === path || (path === "/setup" && location.pathname.startsWith("/exam"));

    if (appLoading || catalogLoading) {
        return (
            <div style={{ 
                height: '100vh', width: '100vw', background: '#0a0f1a', 
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <Spinner />
            </div>
        );
    }

    if (!catalogReady && configError) {
        return (
            <div style={{
                minHeight: '100vh', width: '100%', background: '#0a0f1a', color: '#94a3b8',
                display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center'
            }}>
                <div>
                    <h2 style={{ color: '#f8fafc', margin: '0 0 8px' }}>Could not load certifications</h2>
                    <p style={{ margin: '0 0 20px' }}>Check your connection and try again.</p>
                    <button type="button" onClick={retryConfigs} style={{ minHeight: 42, padding: '0 18px', border: 0, borderRadius: 8, background: '#3b82f6', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (catalogReady && CERTS.length === 0) {
        return (
            <div style={{ minHeight: '100vh', width: '100%', background: '#0a0f1a', color: '#94a3b8', display: 'grid', placeItems: 'center', padding: 24, textAlign: 'center' }}>
                <div><h2 style={{ color: '#f8fafc', margin: '0 0 8px' }}>No certifications available</h2><p style={{ margin: 0 }}>There are no active certifications to study right now.</p></div>
            </div>
        );
    }

    return (
        <DashboardContainer>
            <AppShellScrollLock />
            <AnimatePresence>
                {isChangingCert && (
                    <Overlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ zIndex: 9999 }}>
                        <Spinner />
                    </Overlay>
                )}
            </AnimatePresence>

            {/* Sidebar is hidden during an active exam so the question column
                gets the full viewport width — see also TopBar below. */}
            {!isFullBleed && (
                <Sidebar ref={navRef}>
                    <SidebarHeader>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <img src={logoImg} alt="ExamNova" style={{ width: 32, height: 32, filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.5))', flexShrink: 0 }} />
                            <h1 className="brand-title" style={{ fontSize: '1.4rem', letterSpacing: 0, textTransform: 'none', background: 'linear-gradient(to right, #fff, #94a3b8)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', fontWeight: 800, whiteSpace: 'nowrap' }}>ExamNova</h1>
                        </div>
                    </SidebarHeader>

                    <NavItemStyled to="/" $active={isActive("/")} data-main-nav-active={isActive("/") ? "true" : undefined}><LayoutDashboard size={18} /> Dashboard</NavItemStyled>
                    {FEATURES.study && (
                        <NavItemStyled to={roadmapPath} $active={isStudy} data-main-nav-active={isStudy ? "true" : undefined}>
                            <Map size={18} /> Study
                        </NavItemStyled>
                    )}
                    <NavItemStyled to="/setup" $active={isActive("/setup")} data-main-nav-active={isActive("/setup") ? "true" : undefined}>
                        <Activity size={18} /> Practice
                    </NavItemStyled>
                    {FEATURES.labs && (
                        <NavItemStyled to="/labs" $active={location.pathname.startsWith("/labs")} data-main-nav-active={location.pathname.startsWith("/labs") ? "true" : undefined}>
                            <FlaskConical size={18} /> Labs
                        </NavItemStyled>
                    )}

                    <NavItemStyled to="/analytics" $active={isActive("/analytics")} data-main-nav-active={isActive("/analytics") ? "true" : undefined}>
                        <BarChart2 size={18} /> Analytics
                    </NavItemStyled>
                    <SidebarSpacer />
                </Sidebar>
            )}

            {/* ── Main Dashboard Area ── */}
            <MainContentArea $isExam={isFullBleed} $isStudy={isStudy}>
                {!isFullBleed && (
                <TopBar>
                    <div style={{ display: 'flex', gap: 'clamp(16px, 3vw, 40px)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
                        <CertSelector
                            ref={certRef}
                            role="button"
                            tabIndex={0}
                            aria-expanded={certDropdownOpen}
                            onClick={() => setCertDropdownOpen(!certDropdownOpen)}
                            onKeyDown={(event) => {
                                if (event.target !== event.currentTarget) return;
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setCertDropdownOpen(open => !open);
                                }
                            }}
                        >
                            <div className="cert-info">
                                <div className="label">ACTIVE CERTIFICATION:</div>
                                <div className="name">{activeCert}</div>
                            </div>
                            <ChevronDown size={14} color="#64748b" style={{ marginLeft: 8 }} />
                            
                            <AnimatePresence>
                                {certDropdownOpen && (
                                    <CertDropdownMenu
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        onClick={e => e.stopPropagation()}
                                    >
                                        <SearchWrapper>
                                            <Search size={14} color="#64748b" />
                                            <input 
                                                placeholder="Search certs..." 
                                                value={certSearch}
                                                onChange={e => setCertSearch(e.target.value)}
                                                autoFocus
                                            />
                                        </SearchWrapper>

                                        <ScrollableArea>
                                            {(() => {
                                                const filtered = CERTS.filter(c => 
                                                    c.id.toLowerCase().includes(certSearch.toLowerCase()) || 
                                                    c.name.toLowerCase().includes(certSearch.toLowerCase())
                                                );

                                                if (filtered.length === 0) {
                                                    return <div style={{ padding: 20, textAlign: 'center', color: '#475569', fontSize: '0.8rem' }}>No certifications match your search.</div>;
                                                }

                                                // Dynamic Categorization based on API metadata
                                                const groups = filtered.reduce((acc, c) => {
                                                    let vendor = c.vendor || "Others";
                                                    if (vendor.trim().toLowerCase() === "other") vendor = "Others";
                                                    if (!acc[vendor]) acc[vendor] = [];
                                                    acc[vendor].push(c);
                                                    return acc;
                                                }, {});

                                                // Pin active cert to top of its category
                                                Object.keys(groups).forEach(cat => {
                                                    groups[cat].sort((a, b) => {
                                                        if (a.id === activeCert) return -1;
                                                        if (b.id === activeCert) return 1;
                                                        return a.id.localeCompare(b.id);
                                                    });
                                                });

                                                // Dynamic sorting: Pin AWS to top, Others to bottom, others alphabetical
                                                const orderedCats = Object.keys(groups).sort((a,b) => {
                                                    if (a === "AWS") return -1;
                                                    if (b === "AWS") return 1;
                                                    if (a === "Others") return 1;
                                                    if (b === "Others") return -1;
                                                    return a.localeCompare(b);
                                                });

                                                return orderedCats.map(cat => (
                                                    <div key={cat}>
                                                        <CategoryHeader>
                                                            {cat} <span>{groups[cat].length}</span>
                                                        </CategoryHeader>
                                                        {groups[cat].map(c => (
                                                            <MenuBtn 
                                                                key={c.id}
                                                                onClick={() => {
                                                                    if (c.id === activeCert) { setCertDropdownOpen(false); return; }
                                                                    if (isOnExam) {
                                                                        setPendingCert(c.id);
                                                                        setCertDropdownOpen(false);
                                                                    } else {
                                                                        setActiveCert(c.id);
                                                                        setCertDropdownOpen(false);
                                                                        setCertSearch("");
                                                                    }
                                                                }}
                                                                style={{ 
                                                                    background: activeCert === c.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                                                                    color: activeCert === c.id ? '#f8fafc' : '#94a3b8',
                                                                    flexDirection: 'column',
                                                                    alignItems: 'flex-start',
                                                                    gap: 2,
                                                                    padding: '8px 12px'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                                                                    <GraduationCap size={14} style={{ opacity: activeCert === c.id ? 1 : 0.5 }} />
                                                                    <span style={{ fontWeight: 800 }}>{c.id}</span>
                                                                </div>
                                                                <div style={{ fontSize: '0.65rem', opacity: 0.6, paddingLeft: 24, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', textAlign: 'left' }}>
                                                                    {c.name}
                                                                </div>
                                                            </MenuBtn>
                                                        ))}
                                                    </div>
                                                ));
                                            })()}
                                        </ScrollableArea>
                                    </CertDropdownMenu>
                                )}
                            </AnimatePresence>
                        </CertSelector>

                        <TelemetryItem
                            type="button"
                            onClick={() => { setDateSaveError(false); setIsDatePickerOpen(true); }}
                        >
                            <div className="icon"><Target size={18} /></div>
                            <div>
                                <div className="label">EXAM DATE:</div>
                                <div className="value">
                                    {savingDate ? "Syncing..." : dateSaveError ? "Could not save — try again" : (() => {
                                        if (!examDate) return "Set exam date →";
                                        const parts = typeof examDate === 'string'
                                            ? examDate.match(/^(\d{4})-(\d{2})-(\d{2})(?:T.*)?$/) : null;
                                        const d = parts ? new Date(`${parts[1]}-${parts[2]}-${parts[3]}T00:00:00`) : null;
                                        if (!d || Number.isNaN(d.getTime()) || d.getFullYear() !== Number(parts[1])
                                            || d.getMonth() + 1 !== Number(parts[2]) || d.getDate() !== Number(parts[3])) {
                                            return "Set exam date →";
                                        }
                                        const fmt = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                                        const now = new Date();
                                        // Saved dates are local calendar days. Compare day numbers
                                        // so UTC parsing and 23/25-hour DST days cannot shift them.
                                        const dayNumber = date => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
                                        const diff = Math.round((dayNumber(d) - dayNumber(now)) / 86400000);
                                        return `${fmt} (${diff >= 0 ? `${diff} Days Left` : 'PASSED'})`;
                                    })()}
                                </div>
                            </div>
                        </TelemetryItem>


                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                        <ProfileColumn>
                            <ProfileChip
                                ref={profileRef}
                                role="button"
                                tabIndex={0}
                                aria-expanded={dropdownOpen}
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                onKeyDown={(event) => {
                                    if (event.target !== event.currentTarget) return;
                                    if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        setDropdownOpen(open => !open);
                                    }
                                }}
                                $color={dashboardData?.preferences?.profileColor}
                            >
                                <div className="name-info">
                                    <div className="user">{displayName}</div>
                                </div>
                                <div className="avatar">{initial}</div>

                                <AnimatePresence>
                                    {dropdownOpen && (
                                        <DropdownMenu
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: 10 }}
                                            onClick={e => e.stopPropagation()}
                                        >
                                            <MenuBtn onClick={() => { navigate('/profile'); setDropdownOpen(false); }}>
                                                <User size={16} /> Profile
                                            </MenuBtn>
                                            <div style={{ height: 1, background: '#1e293b', margin: '4px 0' }} />
                                            <MenuBtn className="danger" onClick={onLogout}>
                                                <LogOut size={16} /> Sign Out
                                            </MenuBtn>
                                        </DropdownMenu>
                                    )}
                                </AnimatePresence>
                            </ProfileChip>
                        </ProfileColumn>
                </div>
            </TopBar>
                )}

                <OutletFrame ref={pageRef} data-app-scroll $isExam={isFullBleed} $isStudy={isStudy} $isNode={isNodeBriefing} $isSetup={location.pathname === '/setup'}>
                    <Outlet />
                </OutletFrame>
            </MainContentArea>

            {/* ── Exam-In-Progress Cert Switch Confirmation ── */}
            {pendingCert && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: '#0d1117', border: '1px solid #1e293b',
                        borderRadius: 16, padding: 'clamp(24px, 4vw, 36px) clamp(20px, 3vw, 32px)', width: 'min(420px, 92vw)',
                        boxShadow: '0 25px 60px -12px rgba(0,0,0,0.9)'
                    }}>
                        <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#ef444420', border: '1px solid #ef444440', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', color: '#ef4444' }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        </div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '1.2rem', fontWeight: 900, color: '#f8fafc', textAlign: 'center' }}>Leave Assessment?</h3>
                        <p style={{ margin: '0 0 28px', color: '#94a3b8', fontSize: '0.9rem', textAlign: 'center', lineHeight: 1.6 }}>
                            Switching to <span style={{ color: '#f8fafc', fontWeight: 800 }}>{pendingCert}</span> will abandon your current session. Progress will not be saved.
                        </p>
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button
                                onClick={() => setPendingCert(null)}
                                disabled={switchingCert}
                                style={{ flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #1e293b', background: 'transparent', color: '#94a3b8', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}
                            >
                                Keep Going
                            </button>
                            <button
                                disabled={switchingCert}
                                onClick={async () => {
                                    setSwitchingCert(true);
                                    // Navigate FIRST so the exam page unmounts before
                                    // activeCert changes — prevents a flash of wrong-cert
                                    // content on the still-mounted exam component.
                                    navigate('/');
                                    // Abandon the session so it doesn't appear as
                                    // "resume" on the dashboard when the user returns.
                                    if (currentSessionId) {
                                        try { await clearSession(currentSessionId); } catch (_) {}
                                    }
                                    setActiveCert(pendingCert);
                                    setCertSearch("");
                                    setPendingCert(null);
                                    setSwitchingCert(false);
                                }}
                                style={{ flex: 1, padding: '12px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #ef4444, #b91c1c)', color: '#fff', fontWeight: 800, cursor: switchingCert ? 'not-allowed' : 'pointer', fontSize: '0.9rem', opacity: switchingCert ? 0.6 : 1 }}
                            >
                                {switchingCert ? 'Leaving…' : 'Leave & Switch'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <TacticalDatePicker 
                isOpen={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                selectedDate={examDate}
                onSelect={async (newDate) => {
                    setSavingDate(true);
                    setDateSaveError(false);
                    try {
                        await setExamDate(newDate);
                    } catch (_) {
                        setDateSaveError(true);
                    } finally {
                        setSavingDate(false);
                    }
                }}
            />
        </DashboardContainer>
    );
}
